import { devlog } from "../../util/utils.ts";
import '../inputs/buttons/as-icon-button.ts';
import styleText from './as-feature-controls.css?raw';

// Boolean flags that hide a part of the control set. The canonical spelling
// is the no- form; the hide- forms are deprecated aliases kept for external
// consumers. A flag counts as set when any of its spellings is present.
const HIDE_FLAGS: readonly {
  canonical: string;
  deprecated: readonly string[];
  selector: string;
  className: string;
}[] = [
  { canonical: 'no-info', deprecated: ['hide-info'], selector: '#features-info-button', className: 'hidden' },
  { canonical: 'no-toggle-workpiece-view', deprecated: ['hide-toggle-workpiece-view'], selector: '#features-toggle-workpiece-view-button', className: 'hidden' },
  { canonical: 'no-transfer-mesh', deprecated: ['hide-transfer-mesh'], selector: '#features-transfer-mesh-button', className: 'hidden' },
  // help / settings buttons are not part of the current default set; the
  // flags are kept so slot-era consumers setting them don't error.
  { canonical: 'no-help', deprecated: ['hide-help'], selector: '#features-help-button', className: 'hidden' },
  { canonical: 'no-settings', deprecated: ['hide-settings'], selector: '#features-settings-button', className: 'hidden' },
  { canonical: 'no-defaults', deprecated: ['hide-defaults', 'hide-all-defaults'], selector: '.placement', className: 'hide-defaults' },
];

export class AsFeatureControls extends HTMLElement {
  static readonly tag = 'as-feature-controls' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes() {
    return HIDE_FLAGS.flatMap(flag => [flag.canonical, ...flag.deprecated]);
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsFeatureControls._sheet
    ];

    this.shadowRoot!.innerHTML = /*html*/`
      <as-panel class="placement ${this._flagPresent('no-defaults') ? 'hide-defaults' : ''}">
        <as-icon-button
          id="features-info-button"
          tooltip-placement="left"
          class="default-feature-button ${this._flagPresent('no-info') ? 'hidden' : ''}"
          name="info"
          tooltip-default-text="Inspector"
          tooltip-dict-key="tooltip.feature.info"
          black
          file-name="features/info.svg"
        ></as-icon-button>
        <slot name="top"></slot>
        <slot name="middle">
        </slot>
        <as-icon-button
          id="features-toggle-workpiece-view-button"
          tooltip-placement="left"
          class="default-feature-button ${this._flagPresent('no-toggle-workpiece-view') ? 'hidden' : ''}"
          name="toggle-workpiece-view"
          tooltip-default-text="Show/Hide Workpiece"
          tooltip-dict-key="tooltip.feature.toggle-workpiece-view"
          black
          icon-set="toggle-workpiece-view"
          icon-variant="default"
        ></as-icon-button>
        <as-icon-button 
          id="features-transfer-mesh-button" 
          name="transfer-mesh" 
          tooltip-dict-key="tooltip.feature.transfer-mesh" 
          tooltip-default-text="Transfer Mesh"
          class="default-feature-button ${this._flagPresent('no-transfer-mesh') ? 'hidden' : ''}"
          tooltip-placement="left" 
          black 
          file-name="/features/next-b.svg" 
          ></as-icon-button>
        <slot name="bottom"></slot>
      </as-panel>
    `;  
    
  }

  
  /**
   * When the component is connected to the DOM, create the template
   * and set up the popout button and card
   */
  connectedCallback() {
    window.addEventListener('as-workpiece-visibility-change',this.handleWorkpieceVisibilityChange.bind(this))
    // Note: Info button toggle is handled by as-app via as-button-click event
  }
  handleWorkpieceVisibilityChange = (ev:CustomEvent)=>{
    devlog("workpiece visibility change", ev.detail)
    const visible = ev.detail.visible;
    if(visible){
      this.shadowRoot!.querySelector('#features-toggle-workpiece-view-button')!.setAttribute('icon-variant','default');
    }else{
      this.shadowRoot!.querySelector('#features-toggle-workpiece-view-button')!.setAttribute('icon-variant','hidden');
    }
  }
  /**
   * Clean up when the component is removed
   */
  disconnectedCallback() {
    window.removeEventListener('as-workpiece-visibility-change',this.handleWorkpieceVisibilityChange)
  }
  
  attributeChangedCallback(name: string, _oldValue: string, _newValue: string) {
    const flag = HIDE_FLAGS.find(f => f.canonical === name || f.deprecated.includes(name));
    if (!flag) return;
    const el = this.shadowRoot!.querySelector(flag.selector);
    el?.classList.toggle(flag.className, this._flagPresent(flag.canonical));
  }

  /** True when the flag's canonical no- attribute or any deprecated hide- alias is set. */
  private _flagPresent(canonical: string): boolean {
    const flag = HIDE_FLAGS.find(f => f.canonical === canonical)!;
    return this.hasAttribute(flag.canonical) || flag.deprecated.some(a => this.hasAttribute(a));
  }
}

// Check if the element is already defined before registering it
if (!customElements.get(AsFeatureControls.tag)) {
  customElements.define(AsFeatureControls.tag, AsFeatureControls);
} else {
  console.warn("as-feature-controls already defined");
}  
